import { BLEND_MODES, BaseTexture, Container, IDestroyOptions, Rectangle, Sprite, Texture } from "pixi.js"
import { Coordinate, Light, TileSize } from "game-common/models"
import { TileEffect } from "game-common/map/map"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import {
    OrganicCaveFeatures, OrganicRock, distanceToPolyline, organicOreAt, organicRockIsBreakable, organicRockIsSolid,
} from "game-common/room/cave/organic_cave"
import { BASALT, ROCK_CHUNK, ROCK_MARGIN, RockView, paintRock, paintRockLayers, rockDirtyRect, rockMassConfig } from "./rock_mass"

/**
 * THE ORGANIC CAVE, ON SCREEN: the rock mass (rock_mass.ts paints it), its crystals and their glow, the
 * player's warm torch glow, and the chips a pickaxe knocks loose. Everything here keys off the room's
 * `organicRock` layer; in any other room the layer is empty and invisible.
 *
 * WHERE THE ROCK STANDS is read from the entity-tile layer itself (a mined cell's tile is deleted from it),
 * polled through the provider's removal version once a frame -- a mined cell never triggers a full map
 * render, so a layer that only re-synced after one would never see it.
 *
 * BAKING. The room is cut into ROCK_CHUNK pieces. A piece with nothing but solid rock in reach of it is the
 * column texture and nothing else, and the column texture repeats once per piece, so every such piece
 * shares ONE texture: a fresh cave costs a handful of real bakes (the chamber, the pockets, the mouth). A
 * swing repaints only rockDirtyRect round the cell that changed, into the pieces it touches.
 */

/** The player's torch in an organic cave: reach, brightness at the heart, rings of falloff. */
export const ORGANIC_TORCH = { radius: 240, intensity: 0.85, falloff: 4, glow: 0.22 }
export const organicTorch = (): typeof ORGANIC_TORCH => {
    if (typeof window === "undefined") return ORGANIC_TORCH
    const w = window as any
    if (w.organicTorch === undefined) w.organicTorch = ORGANIC_TORCH
    return w.organicTorch && typeof w.organicTorch === "object" ? w.organicTorch : ORGANIC_TORCH
}

const BUDGET_MS = 4
/** Above every body (chips fly in front of the swinging miner), below the ADD glows. */
const CHIP_Z = 870000
/** In the world surface, so the darkness mask still governs it; ADD so it only ever brightens. */
const GLOW_Z = 880001

interface Chunk {
    x: number
    y: number
    sprite: Sprite
    canvas?: HTMLCanvasElement
    /** The overhanging top (rock_mass.ts paintRockLayers' topRim), drawn in front of bodies standing behind it. */
    topRim?: RimPlane
    /** A thin wall's upper face (faceRim): in front of a body behind the wall, behind one in front of it. */
    faceRim?: RimPlane
}

/** One rim canvas cut into RIM_BAND-row sprites, each sorted with bodies by the bottom of its rows (+ `lift`). */
interface RimPlane {
    canvas: HTMLCanvasElement
    bands: Sprite[]
}

/** Rows per depth-sorted strip of a chunk's rim. */
const RIM_BAND = 8

interface Chip {
    sprite: Sprite
    vx: number
    vy: number
    ground: number
    life: number
    age: number
}

const toTint = (c: readonly number[]) => (c[0] << 16) | (c[1] << 8) | c[2]

const canvasOf = (w: number, h: number): HTMLCanvasElement | undefined => {
    if (typeof document === "undefined") return undefined
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    return canvas
}

/** A soft white disc, tinted per use. */
let glowTexture: Texture | undefined
const glow = (): Texture => {
    if (glowTexture) return glowTexture
    const canvas = canvasOf(128, 128)
    const context = canvas?.getContext("2d")
    if (!canvas || !context) return (glowTexture = Texture.WHITE)
    const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64)
    gradient.addColorStop(0, "rgba(255,255,255,1)")
    gradient.addColorStop(0.35, "rgba(255,255,255,0.45)")
    gradient.addColorStop(1, "rgba(255,255,255,0)")
    context.fillStyle = gradient
    context.fillRect(0, 0, 128, 128)
    return (glowTexture = Texture.from(canvas))
}

// ---------------------------------------------------------------- crystals, drawn as pixel art
const CRYSTAL = [[18, 58, 74], [31, 111, 136], [63, 176, 198], [142, 230, 239], [230, 255, 255]]
const CRYSTAL_W = 40, CRYSTAL_H = 44

const fillPolygon = (rgba: Uint8ClampedArray, w: number, h: number, points: Array<[number, number]>, c: number[]) => {
    const xs = points.map(p => p[0]), ys = points.map(p => p[1])
    for (let y = Math.max(0, Math.floor(Math.min(...ys))); y <= Math.min(h - 1, Math.ceil(Math.max(...ys))); y++) {
        for (let x = Math.max(0, Math.floor(Math.min(...xs))); x <= Math.min(w - 1, Math.ceil(Math.max(...xs))); x++) {
            let inside = false
            const px = x + 0.5, py = y + 0.5
            for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
                const [xi, yi] = points[i], [xj, yj] = points[j]
                if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside
            }
            if (!inside) continue
            const o = (y * w + x) * 4
            rgba[o] = c[0]; rgba[o + 1] = c[1]; rgba[o + 2] = c[2]; rgba[o + 3] = 255
        }
    }
}

/** A cluster of shards on a base line, the cave's crystal: the same shape as the mockup's. */
export const crystalPixels = (scale: number): Uint8ClampedArray => {
    const rgba = new Uint8ClampedArray(CRYSTAL_W * CRYSTAL_H * 4)
    const shards: Array<[number, number, number]> = [[-1, -0.08, 30], [-8, -0.55, 20], [6, 0.45, 18], [-12, -1.0, 12], [11, 0.9, 11]]
    for (const [bx, angle, length] of shards) {
        const L = length * scale, width = 4.2 * (0.6 + 0.4 * scale)
        const cx = CRYSTAL_W / 2 + bx * scale, cy = CRYSTAL_H - 4
        const tx = cx + Math.sin(angle) * L, ty = cy - Math.cos(angle) * L
        const nx = Math.cos(angle) * width, ny = Math.sin(angle) * width
        const mx = cx + (tx - cx) * 0.72, my = cy + (ty - cy) * 0.72
        const left: Array<[number, number]> = [[cx - nx, cy - ny], [mx - nx, my - ny], [tx, ty], [cx, cy]]
        const right: Array<[number, number]> = [[cx, cy], [tx, ty], [mx + nx, my + ny], [cx + nx, cy + ny]]
        fillPolygon(rgba, CRYSTAL_W, CRYSTAL_H, [...left, ...right.slice(1)], CRYSTAL[0])
        fillPolygon(rgba, CRYSTAL_W, CRYSTAL_H, left.map(([x, y]) => [x + 0.8, y] as [number, number]), CRYSTAL[3])
        fillPolygon(rgba, CRYSTAL_W, CRYSTAL_H, right.map(([x, y]) => [x - 0.8, y] as [number, number]), CRYSTAL[2])
        const steps = Math.ceil(L)
        for (let i = 0; i <= steps; i++) {
            const x = Math.round(cx + (tx - cx) * i / steps - 0.5), y = Math.round(cy + (ty - cy) * i / steps - 0.5)
            if (x < 0 || y < 0 || x >= CRYSTAL_W || y >= CRYSTAL_H) continue
            const o = (y * CRYSTAL_W + x) * 4
            rgba[o] = CRYSTAL[1][0]; rgba[o + 1] = CRYSTAL[1][1]; rgba[o + 2] = CRYSTAL[1][2]; rgba[o + 3] = 255
        }
        const gx = Math.round(mx - nx * 0.4 - 0.5), gy = Math.round(my - 0.5)
        if (gx >= 0 && gy >= 0 && gx < CRYSTAL_W && gy < CRYSTAL_H) {
            const o = (gy * CRYSTAL_W + gx) * 4
            rgba[o] = CRYSTAL[4][0]; rgba[o + 1] = CRYSTAL[4][1]; rgba[o + 2] = CRYSTAL[4][2]; rgba[o + 3] = 255
        }
    }
    return rgba
}

const textureFromPixels = (rgba: Uint8ClampedArray, w: number, h: number): Texture => {
    const canvas = canvasOf(w, h)
    const context = canvas?.getContext("2d")
    if (!canvas || !context) return Texture.EMPTY
    context.putImageData(new ImageData(rgba, w, h), 0, 0)
    return Texture.from(canvas)
}

export class RockMassLayer extends Container {
    private provider?: TileMapMetaProvider
    private roomId?: string
    private cols = 0
    private rows = 0
    private codes: string[][] = []
    private solidCells = new Uint8Array(0)
    private damageCells = new Map<number, number>()
    private features?: OrganicCaveFeatures
    private poolsPx: Array<{ x: number; y: number; rx: number; ry: number }> = []
    private removalVersion = -1
    private presentationVersion = -1
    private lavaCells = new Uint8Array(0)
    private faceHeight = 36

    private chunks = new Map<string, Chunk>()
    private bakeQueue: string[] = []
    private dirty: Array<{ x: number; y: number; w: number; h: number }> = []
    private uniform?: Texture

    private crystalSprites: Sprite[] = []
    private crystalGlows: Sprite[] = []
    private crystalLights: Light[] = []
    private torchGlow?: Sprite
    private chipLayer = new Container()
    private chips: Chip[] = []
    private lastTick = 0

    constructor() {
        super()
        this.zIndex = 0.4 // above every ground tile layer (0), below floor-opening edges (0.5) and every body
        this.interactiveChildren = false
        this.chipLayer.zIndex = CHIP_Z
        this.chipLayer.interactiveChildren = false
        this.visible = false
    }

    /** True in an organic cave. */
    get active(): boolean {
        return !!this.roomId
    }

    private readonly view: RockView = {
        solid: (cx, cy) => cx < 0 || cy < 0 || cx >= this.cols || cy >= this.rows || this.solidCells[cy * this.cols + cx] === 1,
        ore: (cx, cy) => organicOreAt(this.codes[cx]?.[cy]),
        dug: (cx, cy) => organicRockIsBreakable(this.codes[cx]?.[cy]) && !this.view.solid(cx, cy),
        pocket: (cx, cy) => this.codes[cx]?.[cy] === OrganicRock.Pocket,
        damage: (cx, cy) => this.damageCells.get(cy * this.cols + cx) ?? 0,
        veinDistance: (px, py, material) => {
            let best = Infinity
            for (const vein of this.features?.veins ?? []) {
                if (vein.material === material) best = Math.min(best, distanceToPolyline(px / TileSize, py / TileSize, vein.points) * TileSize)
            }
            return best
        },
        mouth: (cx, cy) => this.codes[cx]?.[cy] === OrganicRock.Mouth,
        lava: (cx, cy) => cx >= 0 && cy >= 0 && cx < this.cols && cy < this.rows && this.lavaCells[cy * this.cols + cx] === 1,
        pools: () => this.poolsPx,
    }

    /** Whether the rock stands at (x, y) right now, per the provider. */
    private standing = (x: number, y: number): boolean => {
        const code = this.codes[x]?.[y]
        if (!organicRockIsSolid(code)) return false
        if (!organicRockIsBreakable(code)) return true
        const tile = this.provider?.entityTileMap()?.[x]?.[y]
        return !!tile && tile !== "0"
    }

    /** Once a frame, after the frame's map deltas are applied. `player` is where the torch glow goes. */
    tick = (provider: TileMapMetaProvider | undefined, player?: Coordinate): void => {
        const layer = provider?.getOrganicRockLayer?.()
        if (!provider || !layer || !rockMassConfig().enabled) {
            if (this.roomId) this.reset()
            return
        }
        if (provider !== this.provider || provider.roomId !== this.roomId) this.load(provider, layer)
        const version = provider.getRemovalVersion()
        if (version !== this.removalVersion) {
            this.removalVersion = version
            this.refreshStanding()
        }
        // Lava arrives as map deltas (the LAVA campaign paints it into a tunnel when a pocket is broken).
        const presentation = provider.getPresentationVersion()
        if (presentation !== this.presentationVersion) {
            this.presentationVersion = presentation
            this.refreshLava()
        }
        this.bake()
        this.updateTorchGlow(player)
        this.updateChips()
    }

    private load = (provider: TileMapMetaProvider, layer: string[][]): void => {
        this.reset()
        this.provider = provider
        this.roomId = provider.roomId
        this.cols = provider.width()
        this.rows = provider.height()
        this.codes = layer
        this.features = provider.getOrganicCave?.()
        this.faceHeight = this.features?.faceHeight ?? rockMassConfig().faceHeight
        this.poolsPx = (this.features?.pools ?? []).map(p => ({ x: p.x * TileSize, y: p.y * TileSize, rx: p.rx * TileSize, ry: p.ry * TileSize }))
        this.solidCells = new Uint8Array(this.cols * this.rows)
        for (let y = 0; y < this.rows; y++) for (let x = 0; x < this.cols; x++) {
            this.solidCells[y * this.cols + x] = this.standing(x, y) ? 1 : 0
        }
        this.removalVersion = provider.getRemovalVersion()
        this.lavaCells = new Uint8Array(this.cols * this.rows)
        this.presentationVersion = -1
        this.visible = true

        const per = ROCK_CHUNK / TileSize
        const reach = Math.ceil(ROCK_MARGIN / TileSize) + 1
        const across = Math.ceil(this.cols / per), down = Math.ceil(this.rows / per)
        for (let cy = 0; cy < down; cy++) for (let cx = 0; cx < across; cx++) {
            const sprite = new Sprite(Texture.EMPTY)
            sprite.position.set(cx * ROCK_CHUNK, cy * ROCK_CHUNK)
            this.addChild(sprite)
            const key = `${cx},${cy}`
            this.chunks.set(key, { x: cx, y: cy, sprite })
            let uniform = true
            for (let y = cy * per - reach; y < (cy + 1) * per + reach && uniform; y++) {
                for (let x = cx * per - reach; x < (cx + 1) * per + reach && uniform; x++) {
                    if (!this.view.solid(x, y)) uniform = false
                }
            }
            if (uniform) sprite.texture = this.uniformTexture()
            else this.bakeQueue.push(key)
        }
        this.placeCrystals()
    }

    /** The column texture alone: what every chunk buried in solid rock looks like. */
    private uniformTexture = (): Texture => {
        if (this.uniform) return this.uniform
        const solid: RockView = { ...this.view, solid: () => true, damage: () => 0, ore: () => undefined, mouth: () => false, pools: () => [] }
        const rgba = paintRock(solid, -ROCK_MARGIN, -ROCK_MARGIN, ROCK_CHUNK + 2 * ROCK_MARGIN, ROCK_CHUNK + 2 * ROCK_MARGIN,
            ROCK_MARGIN, this.faceHeight)
        return (this.uniform = textureFromPixels(rgba, ROCK_CHUNK, ROCK_CHUNK))
    }

    private refreshLava = (): void => {
        const provider = this.provider
        if (!provider) return
        for (let y = 0; y < this.rows; y++) for (let x = 0; x < this.cols; x++) {
            const i = y * this.cols + x
            const now = provider.isLavaAt(x, y) ? 1 : 0
            if (now === this.lavaCells[i]) continue
            this.lavaCells[i] = now
            // Only the cell's own floor marks change.
            this.dirty.push({ x: x * TileSize, y: y * TileSize, w: TileSize, h: TileSize })
        }
    }

    private refreshStanding = (): void => {
        for (let y = 0; y < this.rows; y++) for (let x = 0; x < this.cols; x++) {
            const i = y * this.cols + x
            const now = this.standing(x, y) ? 1 : 0
            if (now === this.solidCells[i]) continue
            this.solidCells[i] = now
            if (!now) this.damageCells.delete(i)
            this.dirty.push(rockDirtyRect(x, y, this.faceHeight))
        }
    }

    /** A strike or a break on an organic cave's rock: cracks, chips. True when this layer took it. */
    tileEffect = (effect: TileEffect): boolean => {
        if (!this.active || effect.entityTile !== "testore") return false
        const cell = effect.mapRoughCoordinates
        if (!cell) return true
        const i = cell.y * this.cols + cell.x
        const from = effect.strikeFrom
        if (effect.effectType === "damage") {
            const hp = effect.hp, hpMax = effect.hpMax
            const broken = hp !== undefined && hpMax ? 1 - hp / hpMax : Math.min(1, (this.damageCells.get(i) ?? 0) + 0.25)
            this.damageCells.set(i, Math.max(0, Math.min(1, broken)))
            this.dirty.push(rockDirtyRect(cell.x, cell.y, this.faceHeight))
            this.spawnChips(cell, from, 7, 1)
        } else if (effect.effectType === "destroy") {
            this.damageCells.delete(i)
            this.spawnChips(cell, from, 22, 1.6)
        }
        return true
    }

    /**
     * The breakable rock cell drawn at world point `at`, if any. The face is drawn inside its rock's own
     * cell, so the pixel's own cell is the one being pointed at.
     */
    breakableCellAt = (at: Coordinate): Coordinate | undefined => {
        if (!this.active) return undefined
        const x = Math.floor(at.x / TileSize)
        // The pixel's own cell, or -- on a top's overhang -- the rock it overhangs from.
        for (const y of [Math.floor(at.y / TileSize), Math.floor((at.y + this.overhang()) / TileSize)]) {
            if (organicRockIsBreakable(this.codes[x]?.[y]) && this.standing(x, y)) return { x, y }
        }
        return undefined
    }

    /** Crystal lights for the darkness mask: shown only while the crystal is in the player's sight. */
    lights = (): Light[] => (this.active ? this.crystalLights : [])

    // ------------------------------------------------------------ baking
    private bake = (): void => {
        // A swing's repaint first and whole: it is small, and the player is looking at it.
        while (this.dirty.length) this.paintRect(this.dirty.shift()!)
        const start = performance.now()
        while (this.bakeQueue.length && (performance.now() - start < BUDGET_MS)) {
            const chunk = this.chunks.get(this.bakeQueue.shift()!)
            if (chunk) this.paintRect({ x: chunk.x * ROCK_CHUNK, y: chunk.y * ROCK_CHUNK, w: ROCK_CHUNK, h: ROCK_CHUNK })
        }
    }

    private paintRect = (rect: { x: number; y: number; w: number; h: number }): void => {
        const x0 = Math.max(0, rect.x), y0 = Math.max(0, rect.y)
        const x1 = Math.min(this.cols * TileSize, rect.x + rect.w), y1 = Math.min(this.rows * TileSize, rect.y + rect.h)
        const w = x1 - x0, h = y1 - y0
        if (w <= 0 || h <= 0) return
        const overhang = this.overhang()
        const { base, topRim, faceRim } = paintRockLayers(this.view, x0 - ROCK_MARGIN, y0 - ROCK_MARGIN, w + 2 * ROCK_MARGIN,
            h + 2 * ROCK_MARGIN, ROCK_MARGIN, this.faceHeight, overhang)
        const image = new ImageData(base, w, h)
        const topImage = new ImageData(topRim, w, h), faceImage = new ImageData(faceRim, w, h)
        for (let cy = Math.floor(y0 / ROCK_CHUNK); cy * ROCK_CHUNK < y1; cy++) {
            for (let cx = Math.floor(x0 / ROCK_CHUNK); cx * ROCK_CHUNK < x1; cx++) {
                const chunk = this.chunks.get(`${cx},${cy}`)
                if (!chunk) continue
                const context = this.ownCanvas(chunk)?.getContext("2d")
                if (!context) continue
                const ox = cx * ROCK_CHUNK, oy = cy * ROCK_CHUNK
                const sx = Math.max(x0, ox), sy = Math.max(y0, oy)
                const ex = Math.min(x1, ox + ROCK_CHUNK), ey = Math.min(y1, oy + ROCK_CHUNK)
                context.putImageData(image, x0 - ox, y0 - oy, sx - x0, sy - y0, ex - sx, ey - sy)
                chunk.sprite.texture.baseTexture.update()
                // The top's rows stand `overhang` px further south than they are drawn (where their rock really is).
                chunk.topRim = this.paintRim(chunk, chunk.topRim, overhang, topImage, x0 - ox, y0 - oy, sx - x0, sy - y0, ex - sx, ey - sy)
                chunk.faceRim = this.paintRim(chunk, chunk.faceRim, 0, faceImage, x0 - ox, y0 - oy, sx - x0, sy - y0, ex - sx, ey - sy)
            }
        }
    }

    /**
     * Copy a repaint's rim into the chunk's rim canvas and show exactly the bands that now hold any of it. The
     * bands live in the world surface, not this layer, so each sorts against bodies by the bottom of its rows.
     */
    private paintRim = (chunk: Chunk, plane: RimPlane | undefined, lift: number, image: ImageData,
        dx: number, dy: number, sx: number, sy: number, sw: number, sh: number): RimPlane | undefined => {
        const surface = this.parent
        if (!surface) return plane
        if (!plane) {
            const canvas = canvasOf(ROCK_CHUNK, ROCK_CHUNK)
            if (!canvas) return plane
            const base = BaseTexture.from(canvas)
            plane = {
                canvas,
                bands: Array.from({ length: ROCK_CHUNK / RIM_BAND }, (_, band) => {
                    const sprite = new Sprite(new Texture(base, new Rectangle(0, band * RIM_BAND, ROCK_CHUNK, RIM_BAND)))
                    sprite.position.set(chunk.x * ROCK_CHUNK, chunk.y * ROCK_CHUNK + band * RIM_BAND)
                    sprite.zIndex = 100 + chunk.y * ROCK_CHUNK + (band + 1) * RIM_BAND + lift
                    sprite.visible = false
                    surface.addChild(sprite)
                    return sprite
                }),
            }
        }
        const context = plane.canvas.getContext("2d")
        if (!context) return plane
        context.putImageData(image, dx, dy, sx, sy, sw, sh)
        plane.bands[0].texture.baseTexture.update()
        for (let band = Math.floor((dy + sy) / RIM_BAND); band * RIM_BAND < dy + sy + sh && band < plane.bands.length; band++) {
            const rows = context.getImageData(0, band * RIM_BAND, ROCK_CHUNK, RIM_BAND).data
            let any = false
            for (let i = 3; i < rows.length && !any; i += 4) if (rows[i]) any = true
            plane.bands[band].visible = any
            plane.bands[band].zIndex = 100 + chunk.y * ROCK_CHUNK + (band + 1) * RIM_BAND + lift
        }
        return plane
    }

    private overhang = (): number => {
        const live = rockMassConfig().overhang
        return Number.isFinite(live) ? Math.max(0, live) : 16
    }

    /** A chunk's own canvas, made the first time anything in it differs from the shared column texture. */
    private ownCanvas = (chunk: Chunk): HTMLCanvasElement | undefined => {
        if (chunk.canvas) return chunk.canvas
        const canvas = canvasOf(ROCK_CHUNK, ROCK_CHUNK)
        const context = canvas?.getContext("2d")
        if (!canvas || !context) return undefined
        const source = (this.uniformTexture().baseTexture.resource as any)?.source
        if (source) context.drawImage(source, 0, 0)
        chunk.canvas = canvas
        chunk.sprite.texture = Texture.from(canvas)
        return canvas
    }

    // ------------------------------------------------------------ crystals, torch, chips
    private placeCrystals = (): void => {
        const surface = this.parent
        if (!surface) return
        surface.addChild(this.chipLayer)
        ;(this.features?.crystals ?? []).forEach((crystal, i) => {
            const x = crystal.x * TileSize, y = crystal.y * TileSize
            const sprite = new Sprite(textureFromPixels(crystalPixels(crystal.scale), CRYSTAL_W, CRYSTAL_H))
            sprite.anchor.set(0.5, (CRYSTAL_H - 4) / CRYSTAL_H)
            sprite.position.set(x, y)
            sprite.zIndex = 100 + y
            surface.addChild(sprite)
            this.crystalSprites.push(sprite)
            const halo = new Sprite(glow())
            halo.anchor.set(0.5)
            halo.position.set(x, y - 12)
            halo.scale.set((70 + 50 * crystal.scale) / 64)
            halo.tint = 0x0aa6ff
            halo.alpha = 0.5
            halo.blendMode = BLEND_MODES.ADD
            halo.zIndex = GLOW_Z
            surface.addChild(halo)
            this.crystalGlows.push(halo)
            this.crystalLights.push({
                entityId: `organic-crystal:${this.roomId}:${i}`,
                location: { x, y: y - 8 },
                radius: Math.round(90 + 70 * crystal.scale),
                intensity: 0.7,
                sightGated: true,
            })
        })
    }

    private updateTorchGlow = (player?: Coordinate): void => {
        const torch = organicTorch()
        if (!player || !this.parent || !torch.glow) {
            if (this.torchGlow) this.torchGlow.visible = false
            return
        }
        if (!this.torchGlow) {
            this.torchGlow = new Sprite(glow())
            this.torchGlow.anchor.set(0.5)
            this.torchGlow.blendMode = BLEND_MODES.ADD
            this.torchGlow.tint = 0xff5a10
            this.torchGlow.zIndex = GLOW_Z
        }
        if (this.torchGlow.parent !== this.parent) this.parent.addChild(this.torchGlow)
        this.torchGlow.visible = true
        this.torchGlow.alpha = torch.glow
        this.torchGlow.scale.set(torch.radius / 64)
        this.torchGlow.position.set(player.x, player.y - 16)
    }

    /** Rock chips off the struck face, thrown back toward whoever struck it. */
    private spawnChips = (cell: Coordinate, from: Coordinate | undefined, count: number, force: number): void => {
        if (!this.chipLayer.parent && this.parent) this.parent.addChild(this.chipLayer)
        const faceX = (cell.x + 0.5) * TileSize
        const foot = (cell.y + 1) * TileSize
        const away = from ? Math.sign(from.x - faceX) || 0 : 0
        const toward = from ? Math.sign(from.y - foot) : 1
        for (let k = 0; k < count; k++) {
            const sprite = new Sprite(Texture.WHITE)
            const size = Math.random() < 0.3 ? 2 : 1
            sprite.width = size + (Math.random() < 0.2 ? 1 : 0)
            sprite.height = size
            sprite.tint = toTint(BASALT[3 + Math.floor(Math.random() * 4)])
            sprite.position.set(faceX + (Math.random() - 0.5) * 22, foot - 6 - Math.random() * 22)
            this.chipLayer.addChild(sprite)
            this.chips.push({
                sprite,
                vx: (away * 0.6 + (Math.random() - 0.5) * 1.6) * 70 * force,
                vy: (-0.6 - Math.random() * 0.9) * 90 * force + (toward > 0 ? 20 : 0),
                ground: foot + 2 + Math.random() * 14 * force,
                life: 0.6 + Math.random() * 0.6 * force,
                age: 0,
            })
        }
    }

    private updateChips = (): void => {
        const now = performance.now()
        const dt = this.lastTick ? Math.min(0.05, (now - this.lastTick) / 1000) : 0.016
        this.lastTick = now
        if (!this.chips.length) return
        this.chips = this.chips.filter(chip => {
            chip.age += dt
            if (chip.age >= chip.life) {
                chip.sprite.destroy()
                return false
            }
            if (chip.sprite.y < chip.ground || chip.vy < 0) {
                chip.vy += 520 * dt
                chip.sprite.x += chip.vx * dt
                chip.sprite.y = Math.min(chip.ground, chip.sprite.y + chip.vy * dt)
                if (chip.sprite.y >= chip.ground && chip.vy > 0) { chip.vy = -chip.vy * 0.25; chip.vx *= 0.5; if (Math.abs(chip.vy) < 30) chip.vy = 0 }
            }
            chip.sprite.alpha = Math.min(1, (chip.life - chip.age) / 0.25)
            return true
        })
    }

    // ------------------------------------------------------------ lifecycle
    private reset = (): void => {
        this.chunks.forEach(chunk => {
            const own = !!chunk.canvas
            chunk.sprite.destroy(own ? { texture: true, baseTexture: true } : undefined)
            for (const plane of [chunk.topRim, chunk.faceRim]) {
                plane?.bands.forEach((band, i) => band.destroy({ texture: true, baseTexture: i === 0 }))
            }
        })
        this.chunks.clear()
        this.bakeQueue = []
        this.dirty = []
        this.damageCells.clear()
        this.crystalSprites.forEach(sprite => sprite.destroy({ texture: true, baseTexture: true }))
        this.crystalSprites = []
        this.crystalGlows.forEach(sprite => sprite.destroy())
        this.crystalGlows = []
        this.crystalLights = []
        this.chips.forEach(chip => chip.sprite.destroy())
        this.chips = []
        if (this.torchGlow) this.torchGlow.visible = false
        this.provider = undefined
        this.roomId = undefined
        this.codes = []
        this.visible = false
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.reset()
        this.uniform?.destroy(true)
        this.uniform = undefined
        this.torchGlow?.destroy()
        this.chipLayer.destroy({ children: true })
        super.destroy(options)
    }
}
